/**
 * Contract of public/data/live.json (specs/04) + strict runtime validation.
 * An invalid payload is REJECTED as a whole: the app keeps its previous state
 * (règle 8 — network/data failure must never break the screen).
 */

export type LiveStatus = "scheduled" | "live" | "paused" | "finished" | "postponed";

export interface LiveEvent {
  readonly type: string; // "goal" | "yellow" | "red" | "subst" | …
  readonly minute: number | null;
  readonly extra?: number; // minutes additionnelles (ex. 45+2 → minute 45, extra 2)
  readonly team: "home" | "away";
  readonly player?: string; // buteur / joueur averti / SORTANT (remplacement)
  readonly playerId?: number; // id API-Football du joueur ci-dessus (→ fiche joueur)
  readonly assist?: string; // passeur (but) / ENTRANT (remplacement)
  readonly assistId?: number; // id API-Football du passeur / entrant
}

/** Statistiques réelles d'un match : clé -> [domicile, extérieur]. */
export type LiveStats = Readonly<Record<string, readonly [number | null, number | null]>>;

/** Un joueur d'une composition (11 ou banc). `grid` = "ligne:colonne" (mini-terrain). */
export interface LineupPlayer {
  readonly num: number | null;
  readonly name: string;
  readonly pos: string | null; // "G" | "D" | "M" | "F"
  readonly grid: string | null; // "2:4" — pour positionner sur le terrain
  readonly id?: number; // id API-Football (jointure avec les stats joueur)
  readonly captain?: boolean; // capitaine (croisé serveur depuis games.captain des stats joueur)
}
/** Composition d'une équipe : formation + 11 de départ + remplaçants + entraîneur. */
export interface TeamLineup {
  readonly formation: string | null; // "4-3-3"
  readonly coach?: string;
  readonly colors?: { readonly primary: string; readonly text: string }; // maillot authentique
  readonly startXI: readonly LineupPlayer[];
  readonly subs?: readonly LineupPlayer[];
}

export interface LiveMatchEntry {
  readonly id: number; // FIFA id 1..104
  readonly status: LiveStatus;
  readonly minute: number | null;
  readonly addedTime?: number; // arrêts de jeu en cours (status.extra) — ex. minute 90, addedTime 4 → 90+4
  readonly score?: { readonly ft: readonly [number, number]; readonly ht?: readonly [number, number] };
  /** Cumulative score at the end of extra time (120'). */
  readonly extraTime?: { readonly score: readonly [number, number] } | null;
  readonly penalties?: { readonly score: readonly [number, number]; readonly winner: "home" | "away" } | null;
  readonly events?: readonly LiveEvent[];
  readonly stats?: LiveStats;
  readonly lineups?: { readonly home: TeamLineup | null; readonly away: TeamLineup | null };
}

export interface LiveData {
  readonly schemaVersion: 1;
  readonly generatedAt: string;
  readonly source: string;
  readonly matches: readonly LiveMatchEntry[];
}

const STATUSES: ReadonlySet<string> = new Set(["scheduled", "live", "paused", "finished", "postponed"]);

function isScorePair(v: unknown): v is [number, number] {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    v.every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0 && n <= 30)
  );
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function parseStats(raw: unknown): LiveStats | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Record<string, [number | null, number | null]> = {};
  let n = 0;
  for (const [k, v] of Object.entries(raw)) {
    if (n++ >= 24) break; // borne anti-abus (données tierces)
    if (!Array.isArray(v) || v.length !== 2) continue;
    const pair: [number | null, number | null] = [
      typeof v[0] === "number" && Number.isFinite(v[0]) ? v[0] : null,
      typeof v[1] === "number" && Number.isFinite(v[1]) ? v[1] : null,
    ];
    if (pair[0] === null && pair[1] === null) continue;
    out[k.slice(0, 24)] = pair;
  }
  return Object.keys(out).length ? out : undefined;
}

function parsePlayer(raw: unknown): LineupPlayer | null {
  if (!isRecord(raw) || typeof raw.name !== "string") return null;
  return {
    num: typeof raw.num === "number" && Number.isFinite(raw.num) ? raw.num : null,
    name: raw.name.slice(0, 60),
    pos: typeof raw.pos === "string" ? raw.pos.slice(0, 4) : null,
    grid: typeof raw.grid === "string" ? raw.grid.slice(0, 8) : null,
    ...(typeof raw.id === "number" ? { id: raw.id } : {}),
    ...(raw.captain === true ? { captain: true } : {}),
  };
}
function parseTeamLineup(raw: unknown): TeamLineup | null {
  if (!isRecord(raw) || !Array.isArray(raw.startXI)) return null;
  const startXI: LineupPlayer[] = [];
  for (const p of raw.startXI.slice(0, 11)) {
    const pp = parsePlayer(p);
    if (pp) startXI.push(pp);
  }
  if (!startXI.length) return null;
  const subs: LineupPlayer[] = [];
  if (Array.isArray(raw.subs)) {
    for (const p of raw.subs.slice(0, 23)) {
      const pp = parsePlayer(p);
      if (pp) subs.push(pp);
    }
  }
  let colors: TeamLineup["colors"];
  if (isRecord(raw.colors)) {
    const hex = (v: unknown): string | null =>
      typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v) ? v : null;
    const primary = hex(raw.colors.primary);
    if (primary) colors = { primary, text: hex(raw.colors.text) ?? "#ffffff" };
  }
  return {
    formation: typeof raw.formation === "string" ? raw.formation.slice(0, 12) : null,
    ...(typeof raw.coach === "string" ? { coach: raw.coach.slice(0, 60) } : {}),
    ...(colors ? { colors } : {}),
    startXI,
    ...(subs.length ? { subs } : {}),
  };
}
function parseLineups(raw: unknown): LiveMatchEntry["lineups"] | undefined {
  if (!isRecord(raw)) return undefined;
  const home = parseTeamLineup(raw.home);
  const away = parseTeamLineup(raw.away);
  if (!home && !away) return undefined;
  return { home, away };
}

function parseMatch(raw: unknown): LiveMatchEntry | null {
  if (!isRecord(raw)) return null;
  const { id, status, minute } = raw;
  if (typeof id !== "number" || !Number.isInteger(id) || id < 1 || id > 104) return null;
  if (typeof status !== "string" || !STATUSES.has(status)) return null;
  if (minute !== null && minute !== undefined && (typeof minute !== "number" || minute < 0 || minute > 150)) return null;

  let score: LiveMatchEntry["score"];
  if (raw.score !== undefined && raw.score !== null) {
    if (!isRecord(raw.score) || !isScorePair(raw.score.ft)) return null;
    const ht = raw.score.ht;
    if (ht !== undefined && ht !== null && !isScorePair(ht)) return null;
    score = { ft: raw.score.ft, ...(isScorePair(ht) ? { ht } : {}) };
  }
  if ((status === "live" || status === "paused" || status === "finished") && !score) return null;

  let extraTime: LiveMatchEntry["extraTime"] = null;
  if (raw.extraTime !== undefined && raw.extraTime !== null) {
    if (!isRecord(raw.extraTime) || !isScorePair(raw.extraTime.score)) return null;
    extraTime = { score: raw.extraTime.score };
  }
  let penalties: LiveMatchEntry["penalties"] = null;
  if (raw.penalties !== undefined && raw.penalties !== null) {
    if (!isRecord(raw.penalties) || !isScorePair(raw.penalties.score)) return null;
    if (raw.penalties.winner !== "home" && raw.penalties.winner !== "away") return null;
    penalties = { score: raw.penalties.score, winner: raw.penalties.winner };
  }
  const events: LiveEvent[] = [];
  if (raw.events !== undefined) {
    if (!Array.isArray(raw.events)) return null;
    for (const e of raw.events) {
      if (!isRecord(e) || typeof e.type !== "string" || (e.team !== "home" && e.team !== "away")) return null;
      // but sans aucune attribution (ni joueur ni id) = fantôme/doublon non répercuté au
      // score (cf. adaptateur) → écarté à la lecture pour ne jamais l'afficher.
      if (e.type === "goal" && typeof e.player !== "string" && typeof e.playerId !== "number") continue;
      const eventMinute = typeof e.minute === "number" ? e.minute : null;
      events.push({
        // bornes anti-abus (données tierces) : défense en profondeur + anti-DoS
        type: e.type.slice(0, 40),
        minute: eventMinute,
        ...(typeof e.extra === "number" && e.extra > 0 ? { extra: Math.min(e.extra, 30) } : {}),
        team: e.team,
        ...(typeof e.player === "string" ? { player: e.player.slice(0, 80) } : {}),
        ...(typeof e.playerId === "number" ? { playerId: e.playerId } : {}),
        ...(typeof e.assist === "string" ? { assist: e.assist.slice(0, 80) } : {}),
        ...(typeof e.assistId === "number" ? { assistId: e.assistId } : {}),
      });
    }
  }
  const stats = parseStats(raw.stats);
  const lineups = parseLineups(raw.lineups);
  return {
    id,
    status: status as LiveStatus,
    minute: typeof minute === "number" ? minute : null,
    ...(typeof raw.addedTime === "number" && raw.addedTime > 0 ? { addedTime: Math.min(raw.addedTime, 30) } : {}),
    ...(score ? { score } : {}),
    extraTime,
    penalties,
    events,
    ...(stats ? { stats } : {}),
    ...(lineups ? { lineups } : {}),
  };
}

/** Strict parse — returns null (and the reason) on ANY structural problem. */
export function parseLiveJson(raw: unknown): { data: LiveData | null; error: string | null } {
  if (!isRecord(raw)) return { data: null, error: "payload non-objet" };
  if (raw.schemaVersion !== 1) return { data: null, error: `schemaVersion inattendue: ${String(raw.schemaVersion)}` };
  if (typeof raw.generatedAt !== "string" || Number.isNaN(Date.parse(raw.generatedAt))) {
    return { data: null, error: "generatedAt invalide" };
  }
  if (typeof raw.source !== "string") return { data: null, error: "source manquante" };
  if (!Array.isArray(raw.matches)) return { data: null, error: "matches manquant" };
  const matches: LiveMatchEntry[] = [];
  const seen = new Set<number>();
  for (const m of raw.matches) {
    const parsed = parseMatch(m);
    if (!parsed) return { data: null, error: `entrée de match invalide: ${JSON.stringify(m).slice(0, 80)}` };
    if (seen.has(parsed.id)) return { data: null, error: `id de match dupliqué: ${parsed.id}` };
    seen.add(parsed.id);
    matches.push(parsed);
  }
  return {
    data: { schemaVersion: 1, generatedAt: raw.generatedAt, source: raw.source.slice(0, 120), matches },
    error: null,
  };
}
